<script setup>
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import { slice } from 'lodash-es';

const props = defineProps({
  members: {
    required: true,
    default: () => [],
  },
  maxTagsToDisplay: {
    type: Number,
    default: Number.MAX_SAFE_INTEGER,
  },
  popoverPosition: {
    type: String,
    default: 'middle',
    validator(value) {
      return ['left', 'middle', 'right'].includes(value);
    },
  },
  popoverItemsWrap: {
    type: Boolean,
    default: true,
  },
});

const popover_pos_target = ref(null);

const state = reactive({
  open: false,
  popover_position_class: 'right-0 top-0',
});

const tags_to_display = computed(() => {
  return slice(props.members, 0, props.maxTagsToDisplay);
});

const remaining_tags_to_display = computed(() => {
  return slice(props.members, props.maxTagsToDisplay, props.members?.length);
});

const show_popover = computed(() => remaining_tags_to_display.value.length);

const popover_panel_classes = computed(() => {
  const classes = [];
  switch (props.popoverPosition) {
    case 'left':
      classes.push('');
      break;
    case 'middle':
      classes.push('left-1/2 -translate-x-1/2');
      break;
    case 'right':
      classes.push('right-0');
      break;
  }

  return classes;
});

function togglePopover(value) {
  const current_position = popover_pos_target.value.getBoundingClientRect();
  const screen_center = {
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  };
  const y_class
    = current_position.y < screen_center.y ? 'top-full' : 'bottom-full';
  state.popover_position_class = `${y_class}`;

  state.open = value;
}
</script>

<template>
  <div class="flex flex-wrap gap-1">
    <div v-for="member in tags_to_display" :key="member" class="flex items-center whitespace-nowrap text-sm rounded-lg border border-gray-300 py-[3px] px-1.5">
      <HawkMembers
        :members="member"
        :has_avatar="true"
        type="label"
        size="tiny"
      />
    </div>
    <Popover
      v-if="show_popover"
      class="relative flex items-center z-1"
      @mouseenter="togglePopover(true)"
      @mouseleave="togglePopover(false)"
      @click="togglePopover(!state.open)"
    >
      <PopoverButton>
        <div class="flex items-center whitespace-nowrap text-xs rounded-lg border border-gray-300 px-1.5 py-[3px]">
          +{{ remaining_tags_to_display?.length }}
        </div>
      </PopoverButton>
      <span
        ref="popover_pos_target"
        class="absolute top-0 left-0 w-full h-full pointer-events-none"
      />

      <div v-if="state.open">
        <PopoverPanel
          static
          class="absolute z-1 flex w-screen max-w-min"
          :class="[state.popover_position_class, popover_panel_classes]"
        >
          <div
            class="w-56 flex-shrink-0 rounded-xl bg-white p-1.5 text-sm leading-6 shadow-lg ring-1 ring-gray-900/5 max-h-60 scrollbar"
            :class="{ 'flex flex-wrap gap-1': popoverItemsWrap }"
          >
            <div v-for="member in remaining_tags_to_display" :key="member" class="flex items-center whitespace-nowrap text-sm rounded-lg border border-gray-300 py-[3px] px-1.5">
              <HawkMembers
                :members="member"
                :has_avatar="true"
                type="label"
                size="tiny"
              />
            </div>
          </div>
        </PopoverPanel>
      </div>
    </Popover>
  </div>
</template>
